import {
	IconDots,
	IconDownload,
	IconFileUpload,
	IconLink,
	IconLock,
	IconLockOpen,
	IconPencil,
	IconPlus,
	IconRefresh,
	IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Card, Flex, theme } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDeleteAssets } from "@/common/artemis-operations/assets";
import { useProjects } from "@/common/artemis-operations/projects";
import {
	useDeleteReports,
	useReport,
	useUpdateReport,
} from "@/common/artemis-operations/reports";
import type { UpdateReportInput } from "@/common/artemis-sdk/graphql";
import { downloadObjectAsMarkdown, slugify } from "@/common/markdown";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type { Asset, Project } from "@/common/model";
import { ParentType, ReportStatus } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { AssetTable } from "@/components/assets/asset-table";
import { AssetViewerDrawer } from "@/components/assets/asset-viewer-drawer";
import { UploadAssetDialog } from "@/components/assets/upload-asset-dialog";
import {
	BaseContainer,
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { DocumentEditor } from "@/components/document-editor";
import { DocumentEditorDrawer } from "@/components/document-editor-drawer";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { ProjectSelectDrawer } from "@/components/projects/project-select-drawer";
import { ReportDetails } from "@/components/reports/report-details";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/reports/$reportId",
)({ component: ReportPage });

function ReportPage() {
	const { orgSlug, reportId, workspaceId } = Route.useParams();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const notify = useNotify();
	const queryClient = useQueryClient();

	const {
		token: { colorBgContainerDisabled },
	} = theme.useToken();

	const { data: report, isPending, isError, refetch } = useReport(reportId);
	const { data: projects } = useProjects(workspaceId);
	const { mutate: updateReport } = useUpdateReport();
	const { mutate: deleteReports } = useDeleteReports();
	const { mutate: deleteAssets } = useDeleteAssets();

	const [bodyOpen, setBodyOpen] = useState(false);
	const [uploadAssetOpen, setUploadAssetOpen] = useState(false);
	const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
	const [selectProjectOpen, setSelectProjectOpen] = useState(false);
	const { confirm, dialog } = useFrictionConfirm();

	const saveRef = useRef<(input: UpdateReportInput) => void>(() => {});
	saveRef.current = (input: UpdateReportInput) =>
		updateReport(
			{ reportId, input },
			{
				onSuccess: () => {
					notify.success(UPDATE_RESOURCE_INFO_COMPLETED);
					queryClient.invalidateQueries({ queryKey: ["report", reportId] });
				},
				onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
			},
		);
	const save = useCallback(
		(input: UpdateReportInput) => saveRef.current(input),
		[],
	);

	const isSealed = report?.status === ReportStatus.SEALED;

	const downloadRef = useRef(() => {});
	downloadRef.current = () => {
		if (!report) return;
		downloadObjectAsMarkdown(
			report as unknown as Record<string, unknown>,
			slugify(report.title ?? reportId),
			{
				title: report.title,
				bodyKey: "body",
				exclude: ["__typename", "assets"],
			},
		);
	};

	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = () => {
		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_RESOURCE_DESC,
			onConfirm: () => {
				deleteReports([reportId], {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/reports",
							params: { orgSlug, workspaceId },
						});
					},
					onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
				});
			},
		});
	};

	useEffect(() => {
		setTitle(report?.title ?? "Report");
	}, [setTitle, report]);

	useEffect(() => {
		setActions([
			{
				type: "button",
				id: "seal",
				tooltip: isSealed ? "Reopen report" : "Seal report",
				icon: <CustomIcon icon={isSealed ? IconLockOpen : IconLock} />,
				onClick: () =>
					save({
						status: isSealed ? ReportStatus.OPEN : ReportStatus.SEALED,
					}),
			},

			{
				type: "submenu",
				id: "add",
				icon: <CustomIcon icon={IconPlus} />,
				submenu: [
					{
						type: "button",
						id: "add-project",
						label: "Project",
						icon: <CustomIcon icon={IconLink} />,
						disabled: isSealed,
						onClick: () => setSelectProjectOpen(true),
					},
					{
						type: "button",
						id: "add-asset",
						label: "Asset",
						icon: <CustomIcon icon={IconFileUpload} />,
						onClick: () => setUploadAssetOpen(true),
					},
				],
			},

			{
				type: "submenu",
				id: "settings",
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					{
						type: "button",
						id: "download",
						label: "Export",
						tooltip: "Download as Markdown",
						icon: <CustomIcon icon={IconDownload} />,
						onClick: () => downloadRef.current(),
					},

					{
						type: "button",
						id: "refresh",
						label: "Refresh",
						tooltip: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => refetch(),
					},
					{
						type: "button",
						id: "delete",
						label: "Delete",
						danger: true,
						tooltip: "Delete report",
						icon: <CustomIcon icon={IconTrash} />,
						onClick: () => confirmDeleteRef.current(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [setActions, refetch, isSealed, save]);

	const linkedProjects =
		projects?.filter(
			(p): p is NonNullable<Project> =>
				!!p && (report?.projectIds ?? []).includes(p.id),
		) ?? [];

	const assets = report?.assets ?? [];

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!report}>
			<BasePageContainer>
				<Flex vertical gap={16}>
					<BaseContainer title="Details">
						<ReportDetails
							report={report!}
							onSaveTitle={(title) => save({ title })}
							onSaveOwner={(ownerId) => save({ ownerId })}
							projects={linkedProjects}
							projectsPath={`/${orgSlug}/workspaces/${workspaceId}/projects`}
							onEditProjects={
								isSealed ? undefined : () => setSelectProjectOpen(true)
							}
						/>
					</BaseContainer>

					<BaseContainer title="Assets">
						{assets.length > 0 ? (
							<AssetTable
								data={assets}
								onViewAsset={(asset) => setSelectedAsset(asset)}
								onDeleteAssets={(assetIds) =>
									deleteAssets(
										assetIds.map((a) => a.id),
										{
											onSuccess: () => {
												queryClient.invalidateQueries({
													queryKey: ["report", reportId],
												});
												notify.success(DELETE_RESOURCE_INFO_COMPLETED);
											},
											onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
										},
									)
								}
							/>
						) : (
							<CustomEmpty
								description="Assets"
								onAdd={() => setUploadAssetOpen(true)}
							/>
						)}
					</BaseContainer>

					<BaseContainer
						title="Content"
						actions={
							!isSealed && (
								<Button
									type="text"
									icon={<CustomIcon icon={IconPencil} />}
									onClick={() => setBodyOpen(true)}
								>
									Edit
								</Button>
							)
						}
					>
						{report?.body ? (
							<Card
								style={{
									backgroundColor: colorBgContainerDisabled,
									width: "100%",
								}}
							>
								<DocumentEditor
									content={report.body}
									viewMode="preview"
									readOnly
								/>
							</Card>
						) : (
							<CustomEmpty
								description="content"
								onAdd={isSealed ? undefined : () => setBodyOpen(true)}
							/>
						)}
					</BaseContainer>
				</Flex>
			</BasePageContainer>

			<ProjectSelectDrawer
				open={selectProjectOpen}
				onClose={() => setSelectProjectOpen(false)}
				selectedProjectIds={report?.projectIds ?? []}
				onSave={(projectIds) => save({ projectIds })}
			/>

			<DocumentEditorDrawer
				title="Body"
				content={report?.body ?? ""}
				open={bodyOpen}
				readOnly={isSealed}
				onSave={(body) => save({ body })}
				onClose={() => setBodyOpen(false)}
			/>

			<AssetViewerDrawer
				asset={selectedAsset}
				onClose={() => setSelectedAsset(null)}
			/>

			<UploadAssetDialog
				open={uploadAssetOpen}
				onClose={() => setUploadAssetOpen(false)}
				parentId={reportId}
				parentType={ParentType.REPORT}
				workspaceId={workspaceId}
				onUploadComplete={() =>
					queryClient.invalidateQueries({ queryKey: ["report", reportId] })
				}
			/>
			{dialog}
		</QueryGuard>
	);
}
